import { Grid, IconButton, Typography } from "@mui/material";
import TextFieldFormControl from "../../field/TextFieldFormControl/TextFieldFormControl";
import { useTranslation } from "react-i18next";
import ItemRecord from "../../other/ItemRecord/ItemRecord";
import AutocompleteField from "../../field/AutocompleteField";
import FloatField from "../../base/FloatField/FloatField";
import PropTypes from "prop-types";
import { useCallback, useEffect } from "react";
import useItemService from "../../../services/itemService";
import { useForm } from "../../../hooks/useForm";
import {
  MATERIAL_MAIN_BRANCH_NAME,
  DEVICE_MAIN_BRANCH_NAME,
  COUNTER_BRANCH_NAME,
} from "../../../helpers/constants";
import useDialog from "../../../hooks/useDialog";
import SelectPartDialog from "../../dialog/SelectPartDialog";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import useOfferService from "../../../services/offerService";
import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import MiniButton from "../../base/MiniButton/MiniButton";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import useEquipmentService from "../../../services/equipmentService";
import useWarehouseService from "../../../services/warehouseService";
import { isEmptyValue } from "../../../helpers/methods";

const TransactionOutMaterialRecord = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();
  const { prepareItemDataFromBackendForItemRecordComponent } = useItemService();

  const { onChangeAutocompleteFieldWithObjectOptions, onChange, onChangeDate } =
    useForm();

  const { getEquipmentItemRecordById } = useEquipmentService();

  const { getPartItemRecordById } = useOfferService();

  const getPartItemRecordByIdFn = useAsyncFn(getPartItemRecordById);
  const getEquipmentItemRecordByIdFn = useAsyncFn(getEquipmentItemRecordById);

  const { getStockAvailability } = useWarehouseService();

  const [
    openSelectPartDialog,
    onOpenSelectPartDialog,
    onCloseSelectPartDialog,
  ] = useDialog();

  const onChangeLevelItem = (item) => {
    props.onChangeByIndex({
      name: "item",
      value: item,
      index: props.materialIndex,
    });
    props.onCleanPart(props.materialIndex);
  };

  const setItemBranchWithMaterial = useCallback(
    (selectedPartId) => {
      const getItemRecordFn =
        props.itemType === "equipment"
          ? getEquipmentItemRecordByIdFn
          : getPartItemRecordByIdFn;

      getItemRecordFn
        .execute(selectedPartId)
        .then((result) => {
          props.onChangeByIndex({
            name: "item",
            value: prepareItemDataFromBackendForItemRecordComponent(
              result.item
            ),
            index: props.materialIndex,
          });
          if (props.itemType === "equipment") {
            props.onChangeByIndex({
              name: "equipment",
              value: result.id,
              index: props.materialIndex,
            });
            props.onSetEquipmentData(result, props.materialIndex);
          } else {
            props.onChangeByIndex({
              name: "part",
              value: result.id,
              index: props.materialIndex,
            });
            props.onSetPartData(result, props.materialIndex);
          }
          onCloseSelectPartDialog();
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            t("snackbar_alert.occurred_error_during_get_item_data")
          );
        });
    },
    [props.materialIndex]
  );

  const prepreDataToGetAmount = (data) => {
    let retData = {};
    retData.warehouse = props.warehouse;
    retData.part = data.part;
    retData.equipment = data.equipment;
    return retData;
  };

  const getStockAmountAndPrice = useAsync(() => {
    if (
      !isEmptyValue(props.warehouse) &&
      (props.material?.part || props.material?.equipment)
    ) {
      return getStockAvailability(prepreDataToGetAmount(props.material));
    }
    return Promise.resolve([]);
  }, [props.warehouse, props.material?.part, props.material?.equipment]);

  useEffect(() => {
    if (getStockAmountAndPrice.loading) {
      return;
    }
    if (getStockAmountAndPrice.value) {
      props.onSetStockData(props.materialIndex, getStockAmountAndPrice.value);
    }
  }, [getStockAmountAndPrice.loading]);

  const onChangeAutocompleteLocal = useCallback(
    (e, value_object, value_key, state_value_name) => {
      props.onChangeByIndex({
        ...onChangeAutocompleteFieldWithObjectOptions(
          e,
          value_object,
          value_key,
          state_value_name
        ),
        index: props.materialIndex,
      });
      if (state_value_name === "part") {
        props.onSetPartData(value_object, props.materialIndex);
      }
      if (state_value_name === "equipment") {
        props.onSetEquipmentData(value_object, props.materialIndex);
      }
    },
    [props.materialIndex]
  );

  const onChangeLocal = useCallback(
    (e) => {
      props.onChangeByIndex({ ...onChange(e), index: props.materialIndex });
    },
    [props.materialIndex]
  );

  const getItemLabel = () =>
    props.itemType === "equipment"
      ? t("form.transaction_form.equipment")
      : t("form.transaction_form.part");

  const getButtonLabel = () =>
    props.itemType === "equipment"
      ? t("form.transaction_form.add_equipment")
      : t("form.transaction_form.add_part");

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item sm={6} xs={6} textAlign={"start"} display={"flex"}>
            <Typography variant="subtitle2" className="capitalize-first-letter">
              {getItemLabel()}
              {` ${props.materialIndex + 1}`}
            </Typography>

            {props.showButtonDeleteMaterial && (
              <IconButton
                aria-label="delete"
                size="small"
                sx={{ padding: 0, paddingLeft: 0.5, color: "var(--alert)" }}
                onClick={() => {
                  props.onDeleteLocation(props.materialIndex);
                }}
              >
                <RemoveCircleIcon fontSize="inherit" />
              </IconButton>
            )}
          </Grid>
          <Grid item sm={6} xs={6} textAlign={"end"}>
            {props.showButtonAddMaterial && (
              <MiniButton
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineOutlinedIcon />}
                onClick={props.onAddMaterial}
              >
                {getButtonLabel()}
              </MiniButton>
            )}
          </Grid>
        </Grid>
      </Grid>
      {props.showItemRecord ? (
        <Grid item xs={12}>
          <ItemRecord
            itemLevels={props.material["item"]}
            onChangeItem={onChangeLevelItem}
            onChangeAutocompleteFieldWithObjectOptions={
              onChangeAutocompleteLocal
            }
            partValue={props.material["part"]}
            equipmentValue={props.material["equipment"]}
            onCleanPart={props.onCleanPart}
            showMaterialLabel={props.itemType === "part"}
            showEquipmentLabel={props.itemType === "equipment"}
            required={false}
            onClickSearchItem={onOpenSelectPartDialog}
            defaultFirstLevelItemName={
              props.itemType === "equipment"
                ? DEVICE_MAIN_BRANCH_NAME
                : MATERIAL_MAIN_BRANCH_NAME
            }
            defaultSecondLevelItemName={
              props.itemType === "equipment" ? COUNTER_BRANCH_NAME : null
            }
          />
        </Grid>
      ) : props.itemType === "equipment" ? (
        <Grid item xs={3}>
          <AutocompleteField
            name={"equipment"}
            label={t("form.transaction_form.equipment")}
            value={props.material[`equipment`]}
            options={props.poPartList?.value}
            isObjectOption={true}
            optionLabelKey={"equipment_nr"}
            onChange={onChangeAutocompleteLocal}
            addNewValue={false}
            multiple={false}
            required={true}
          />
        </Grid>
      ) : (
        <Grid item xs={3}>
          <AutocompleteField
            name={"part"}
            label={t("form.transaction_form.part")}
            value={props.material[`part`]}
            options={props.poPartList?.value}
            isObjectOption={true}
            optionLabelKey={"part_name"}
            onChange={onChangeAutocompleteLocal}
            addNewValue={false}
            multiple={false}
            required={true}
          />
        </Grid>
      )}
      <Grid item xs={2.4}>
        <FloatField
          name="transaction_amount"
          readOnly={props.readOnly}
          value={props.material[`transaction_amount`]}
          label={t("form.transaction_form.transaction_amount")}
          onChange={onChangeLocal}
          required
          isInvalid={
            props.showTransactionAmountError &&
            (props.material[`part`] || props.material[`equipment`]) &&
            !props.material[`transaction_amount`]
          }
          helperText={
            props.showTransactionAmountError &&
            (props.material[`part`] || props.material[`equipment`]) &&
            !props.material[`transaction_amount`] &&
            t("field_required")
          }
        />
      </Grid>
      <Grid item xs={2.4}>
        <FloatField
          name="stock_amount"
          disabled={true}
          value={props.material[`stock_amount`]}
          label={t("form.reservation_form.stock_amount")}
        />
      </Grid>
      <Grid item xs={2.4}>
        <FloatField
          name="stock_availability"
          disabled={true}
          value={props.material[`stock_availability`]}
          label={t("form.buffer_details_form.stock_amount")}
        />
      </Grid>
      <Grid item xs={2.4}>
        <TextFieldFormControl
          disabled={true}
          value={
            props.material.part_unit
              ? t(`other.units.${props.material.part_unit}`)
              : undefined
          }
          label={t("form.transaction_form.part_unit")}
        />
      </Grid>
      <Grid item xs={2.4}>
        <FloatField
          name="transaction_price"
          disabled={true}
          value={props.material[`transaction_price`]}
          label={t("form.transaction_form.transaction_price")}
        />
      </Grid>
      {openSelectPartDialog && (
        <SelectPartDialog
          open={openSelectPartDialog}
          onClose={onCloseSelectPartDialog}
          onSubmit={setItemBranchWithMaterial}
          searchParams={
            props.warehouse
              ? { item_isnull: false, warehouse_stock: props.warehouse }
              : { item_isnull: false }
          }
          itemType={props.itemType}
        />
      )}
    </Grid>
  );
};

TransactionOutMaterialRecord.propTypes = {
  formValue: PropTypes.object,
  onChange: PropTypes.func,
  onChangeAutocomplete: PropTypes.func,
  onChangeLevelItem: PropTypes.func,
  onChangeDate: PropTypes.func,
  readOnly: PropTypes.bool,
  showItemRecord: PropTypes.bool,
  showButtonAddMaterial: PropTypes.bool,
  onAddMaterial: PropTypes.func,
  materialIndex: PropTypes.number,
  showButtonDeleteMaterial: PropTypes.bool,
  showSearchItemButton: PropTypes.bool,
  itemType: PropTypes.string,
  showTransactionAmountError: PropTypes.bool,
};

TransactionOutMaterialRecord.defaultProps = {
  readOnly: false,
  showItemRecord: true,
  showButtonAddMaterial: false,
  materialIndex: 0,
  showButtonDeleteMaterial: false,
  itemType: "part",
  showSearchItemButton: true,
  showTransactionAmountError: false,
};

export default TransactionOutMaterialRecord;
